import React, { useMemo, useState } from 'react';
import { Box } from '../../components/UI/Box';
import { Button } from '../../components/UI/Button';
import { Flex } from '../../components/UI/Flex';
import { FormControl } from '../../components/UI/FormControl';
import Typography from '../../components/Typography';
import UploadIcon from '../../components/Icons/UploadIcon';
import { PatientService, UpdateAdditionalDataPatientPayload } from '../../services/patient';
import { onlyDigits } from 'src/helpers/string';
import { Progress } from '@chakra-ui/react';
import CheckIcon from '../../components/Icons/CheckIcon';
import ExitIcon from '../../components/Icons/ExitIcon';
import { Spinner } from '../../components/UI/Spinner';
import { DateFromFormats } from 'src/helpers/Date';
import { DateTime } from 'luxon';
import { validateEmail } from 'src/helpers/validateEmail';
import { validateCPF } from 'src/helpers/cpf';
import {
  trackImportPatientsCancelClick,
  trackImportPatientsConfirmClick,
  trackImportPatientsDownloadExampleClick,
  trackImportPatientsFinishClick,
} from 'src/services/tracking/events';

interface ImportPatientsDialogProps {
  onClose: () => void;
  prescriberId: string;
}

enum ImportStatusEnum {
  Importing = 'importing',
  Success = 'success',
  Error = 'error',
}

enum ImportStatusErrorMessagesEnum {
  InvalidCpf = 'Cpf Inválido',
  PatientRegistered = 'Paciente já cadastrado',
}

interface PatientImportStatus {
  name: string;
  status: ImportStatusEnum;
  statusMessage?: ImportStatusErrorMessagesEnum;
}

export function ImportPatientsDialog(props: ImportPatientsDialogProps) {
  const { onClose, prescriberId } = props;
  const [file, setFile] = useState<File | null>(null);
  const [importStatus, setImportStatus] = useState<PatientImportStatus[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isImportingFinished, setIsImportingFinished] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const detectCsvDelimiter = (csvText: string): string => {
    const firstLine = csvText.split('\n')[0];
    const commonDelimiters = [',', ';', '\t', '|'];

    const delimiterCounts = commonDelimiters.map((delimiter) => ({
      delimiter,
      count: (firstLine.match(new RegExp(`\\${delimiter}`, 'g')) || []).length,
    }));

    const detected = delimiterCounts.reduce((prev, current) => (current.count > prev.count ? current : prev));

    return detected.count > 0 ? detected.delimiter : ',';
  };

  const parseCSV = (csvText: string) => {
    const lines = csvText.split('\n').filter((line) => line.trim() !== '');
    const delimiter = detectCsvDelimiter(csvText);

    const headers = lines[0].split(delimiter).map((header) => header.trim());
    const data = lines
      .slice(1)
      .map((line) => {
        const values = line.split(delimiter).map((value) => value.trim());
        const entry: { [key: string]: string } = {};
        headers.forEach((header, index) => {
          entry[header] = values[index] || '';
        });
        return entry;
      })
      .filter((entry) => {
        const hasAnyValue = Object.values(entry).some((value) => value && value.trim() !== '');
        return hasAnyValue;
      });
    return data;
  };

  const handleImport = async () => {
    if (!file || !prescriberId) return;

    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      const patientsToImport = parseCSV(text);

      if (!patientsToImport || patientsToImport.length === 0) {
        alert('Não foi possível encontrar pacientes no arquivo. Verifique o formato do arquivo.');
        setIsImporting(false);
        return;
      }

      const initialStatus = patientsToImport.map((p) => ({
        name: p['Nome Completo'] || 'Nome não encontrado',
        status: ImportStatusEnum.Importing as const,
      }));
      setImportStatus(initialStatus);

      enum CsvFields {
        fullName = 'Nome Completo',
        cellphoneNumber = 'Celular',
        document = 'CPF',
        sex = 'Sexo',
        birthDate = 'Data de Nascimento',
        email = 'Email',
      }
      const validDateFormats = ['dd/MM/yyyy', 'ddMMyyyy', 'dd-MM-yyyy'];

      for (let i = 0; i < patientsToImport.length; i++) {
        const patient = patientsToImport[i];
        try {
          const birthDateOptional = DateFromFormats(patient[CsvFields.birthDate] || '', validDateFormats);
          const emailOptional = patient[CsvFields.email];

          const isMissingPatientDocumentLeftZero = patient[CsvFields.document].length < 11;
          const completePatientDocumentWithLeftZero = patient[CsvFields.document].padStart(11, '0');

          const patientCompleteDocument = isMissingPatientDocumentLeftZero
            ? completePatientDocumentWithLeftZero
            : patient[CsvFields.document];

          if (!validateCPF(patientCompleteDocument)) {
            throw new Error(ImportStatusErrorMessagesEnum.InvalidCpf);
          }

          const newPacient = await PatientService.createPartialPatient(
            {
              fullName: patient[CsvFields.fullName],
              cellphoneNumber: onlyDigits(patient[CsvFields.cellphoneNumber]),
              document: onlyDigits(patientCompleteDocument),
              documentType: 'cpf',
              sex: patient[CsvFields.sex] === 'Masculino' ? 'Masculino' : 'Feminino',
              birthDate: birthDateOptional ? DateTime.fromISO(birthDateOptional).toJSDate() : undefined,
            },
            prescriberId
          );

          const isValidEmail = await validateEmail(emailOptional);
          if (isValidEmail) {
            const newPacientId = newPacient._id;
            const payload: UpdateAdditionalDataPatientPayload = {
              patientEmail: emailOptional,
              patientId: newPacientId,
              address: '',
              city: '',
              complement: '',
              district: '',
              houseNumber: '',
              parentName: '',
              postalCode: '',
              state: '',
            };

            await PatientService.updatePatientAdditionalInformation(prescriberId, payload);
          }

          setImportStatus((prev) =>
            prev.map((statusList, index) =>
              index === i ? { ...statusList, status: ImportStatusEnum.Success } : statusList
            )
          );
        } catch (error: any) {
          setImportStatus((prev) =>
            prev.map((statusList, index) =>
              index === i
                ? {
                    ...statusList,
                    status: ImportStatusEnum.Error,
                    statusMessage:
                      error.message === ImportStatusErrorMessagesEnum.InvalidCpf
                        ? ImportStatusErrorMessagesEnum.InvalidCpf
                        : ImportStatusErrorMessagesEnum.PatientRegistered,
                  }
                : statusList
            )
          );
        }
      }
      setIsImportingFinished(true);
      setIsImporting(false);
    };
    reader.onerror = () => {
      alert('Ocorreu um erro ao ler o arquivo.');
      setIsImporting(false);
    };
    reader.readAsText(file);
  };

  const completedCount = useMemo(() => {
    return importStatus.filter((s) => s.status === ImportStatusEnum.Success || s.status === ImportStatusEnum.Error)
      .length;
  }, [importStatus]);

  const progressValue = useMemo(() => {
    if (importStatus.length === 0) return 0;
    return (completedCount / importStatus.length) * 100;
  }, [completedCount, importStatus.length]);

  const StatusIcon = ({ status }: { status: PatientImportStatus['status'] }) => {
    if (status === ImportStatusEnum.Importing) {
      return <Spinner size="sm" />;
    }
    if (status === ImportStatusEnum.Success) {
      return <CheckIcon variantColor="notification.success" />;
    }
    if (status === ImportStatusEnum.Error) {
      return <ExitIcon variantColor="notification.error" />;
    }
    return null;
  };

  return (
    <Box bg={'surface'} p={'20px'} borderRadius={'10px'} w="400px">
      <Box mb={'20px'}>
        <Typography model="h2" weight="bold" textAlign="center">
          Importar Pacientes
        </Typography>
      </Box>
      {!isImporting && importStatus.length === 0 && (
        <>
          <Box mb={'20px'}>
            <Typography model="body" size="large" weight="regular" color="support.dark">
              Para carregar sua lista de pacientes, escolha um arquivo CSV (UTF-8) separado por:
            </Typography>
            <Box pl={'20px'} mt={'10px'}>
              <Box mb={'5px'}>
                <Typography model="body" size="large" weight="regular" color="support.dark">
                  • , (vírgula)
                </Typography>
              </Box>
              <Box mb={'5px'}>
                <Typography model="body" size="large" weight="regular" color="support.dark">
                  • ; (ponto e vírgula)
                </Typography>
              </Box>
              <Box mb={'5px'}>
                <Typography model="body" size="large" weight="regular" color="support.dark">
                  • (tabulação)
                </Typography>
              </Box>
              <Box mb={'5px'}>
                <Typography model="body" size="large" weight="regular" color="support.dark">
                  • | (pipe)
                </Typography>
              </Box>
            </Box>
          </Box>
          <FormControl>
            <label htmlFor="csv-upload" style={{ cursor: 'pointer' }}>
              <Flex
                border={'2px dashed'}
                borderColor={'primary.main'}
                borderRadius={'10px'}
                p={'20px'}
                textAlign={'center'}
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'center'}
                minH="150px"
              >
                <UploadIcon width={50} />
                <Box mt={'10px'}>
                  <Typography model="body">{file ? file.name : 'Carregar arquivo'}</Typography>
                </Box>
              </Flex>
            </label>
            <input
              data-testid="csv-upload"
              id="csv-upload"
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </FormControl>
          <Flex justifyContent={'center'} my={'20px'}>
            <a
              href="https://mevo-receita-digital-assets.mevosaude.com.br/static/pacientes_exemplo.csv"
              download
              style={{ textDecoration: 'underline', color: '#4fadff' }}
              onClick={() => trackImportPatientsDownloadExampleClick(prescriberId)}
            >
              <Typography model="body" color="primary.main" weight="bold">
                Baixar arquivo de exemplo
              </Typography>
            </a>
          </Flex>
        </>
      )}
      {importStatus.length > 0 && (
        <Box>
          <Box mb={2}>
            <Typography model="body" weight="bold">
              {isImporting ? `Importando... (${completedCount}/${importStatus.length})` : 'Importação Concluída'}
            </Typography>
          </Box>
          <Box mb={4}>
            <Progress value={progressValue} />
          </Box>
          <Box
            maxH="250px"
            overflowY="auto"
            pr={2}
            sx={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'gray.100',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'gray.400',
                borderRadius: '8px',
              },
            }}
          >
            {importStatus.map((s, i) => (
              <Flex key={i} flexDirection="column" mb={3} borderRadius="md" my={2} p={2} bg="gray.100">
                <Flex justifyContent="space-between" alignItems="center" borderRadius="md">
                  <Flex flexDirection="column">
                    <Typography model="body">{s.name}</Typography>
                    {s.status === ImportStatusEnum.Error && s.statusMessage && (
                      <Typography model="body" color="notification.error">
                        {s.statusMessage}
                      </Typography>
                    )}
                  </Flex>
                  <Flex alignItems="center">
                    <StatusIcon status={s.status} />
                    <Box ml={2}>
                      <Typography
                        model="body"
                        color={
                          s.status === ImportStatusEnum.Success
                            ? 'notification.success'
                            : s.status === ImportStatusEnum.Error
                              ? 'notification.error'
                              : 'gray.500'
                        }
                      >
                        {s.status === 'importing' && 'Importando...'}
                        {s.status === 'success' && 'Sucesso!'}
                        {s.status === 'error' && 'Erro'}
                      </Typography>
                    </Box>
                  </Flex>
                </Flex>
              </Flex>
            ))}
          </Box>
        </Box>
      )}
      <Flex justifyContent={'space-between'} mt={4}>
        {isImportingFinished ? (
          <>
            <Button
              onClick={() => {
                trackImportPatientsFinishClick(prescriberId);
                onClose();
              }}
              data-testid="csv-import-button"
              w={'100%'}
              h={'60px'}
              disabled={!file || isImporting}
            >
              {'Concluir'}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                trackImportPatientsCancelClick(prescriberId);
                onClose();
              }}
              variant={'outline'}
              w={'100%'}
              h={'60px'}
              mr={'10px'}
              disabled={isImporting}
            >
              {importStatus.length > 0 ? 'Fechar' : 'Cancelar'}
            </Button>
            <Button
              onClick={() => {
                trackImportPatientsConfirmClick(prescriberId);
                handleImport();
              }}
              data-testid="csv-import-button"
              w={'100%'}
              h={'60px'}
              disabled={!file || isImporting}
            >
              {isImporting ? 'Importando...' : 'Importar'}
            </Button>
          </>
        )}
      </Flex>
    </Box>
  );
}
